import { useState, useRef, ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

interface ImageUploadProps {
  currentImageUrl?: string | null;
  onUpload: (base64Data: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  label?: string;
  maxSizeMB?: number;
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'free';
  className?: string;
  recommendedDimensions?: string; // e.g. "200x200px"
  helpText?: string;
}

export default function ImageUpload({
  currentImageUrl,
  onUpload,
  onDelete,
  label = 'Upload Image',
  maxSizeMB = 10,
  aspectRatio = 'free',
  className = '',
  recommendedDimensions,
  helpText,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const aspectClasses = {
    square: 'aspect-square',
    landscape: 'aspect-video',
    portrait: 'aspect-[3/4]',
    free: 'min-h-32',
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxSizeMB) {
      setError(`Image must be smaller than ${maxSizeMB}MB (current: ${sizeMB.toFixed(1)}MB)`);
      return;
    }

    // Read and convert to base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target?.result as string;
      setPreview(base64);

      setUploading(true);
      try {
        await onUpload(base64);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        setPreview(null);
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsDataURL(file);

    // Clear input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setUploading(true);
    try {
      await onDelete();
      setPreview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setUploading(false);
    }
  };

  const displayImage = preview || currentImageUrl;

  return (
    <div className={className}>
      <label className="label mb-2">{label}</label>

      <div
        className={`relative border-2 border-dashed rounded-lg overflow-hidden transition-colors ${
          uploading ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
        } ${aspectClasses[aspectRatio]}`}
      >
        {displayImage ? (
          // Show current/preview image
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <img
              src={displayImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
            {/* Delete button */}
            {onDelete && !uploading && (
              <button
                onClick={handleDelete}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                title="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {/* Change button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-2 right-2 px-3 py-1 bg-white/90 text-sm font-medium text-gray-700 rounded-lg hover:bg-white transition-colors shadow"
            >
              Change
            </button>
          </div>
        ) : (
          // Show upload prompt
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex flex-col items-center justify-center p-4 text-gray-500 hover:text-primary-600 transition-colors"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
            ) : (
              <>
                <ImageIcon className="w-10 h-10 mb-2" />
                <span className="text-sm font-medium">Click to upload</span>
                <span className="text-xs text-gray-400 mt-1">
                  PNG, JPG, WebP up to {maxSizeMB}MB
                </span>
                {recommendedDimensions && (
                  <span className="text-xs text-primary-500 mt-1">
                    Recommended: {recommendedDimensions}
                  </span>
                )}
              </>
            )}
          </button>
        )}

        {/* Loading overlay */}
        {uploading && displayImage && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help text */}
      {helpText && !error && (
        <p className="text-xs text-gray-500 mt-2">{helpText}</p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}
    </div>
  );
}
